let timezoneSettingsCache = null;

/**
 * Get a shorter timezone label for display
 * @param {string} displayName - Full timezone display name
 * @returns {string} Shortened label
 */
const getShortTimezoneLabel = (displayName) => {
  if (!displayName) return 'UTC';
  
  const match = displayName.match(/\(([^)]+)\)/);
  if (match) {
    return match[1]; 
  }
  
  if (displayName.includes('Eastern Time')) return 'EST/EDT';
  if (displayName.includes('Pacific Time')) return 'PST/PDT';
  if (displayName.includes('Central Time')) return 'CST/CDT';
  if (displayName.includes('Mountain Time')) return 'MST/MDT';
  if (displayName.includes('London')) return 'GMT';
  if (displayName.includes('India')) return 'IST';
  if (displayName.includes('Singapore')) return 'SGT';
  
  return displayName;
};
let timezoneSettingsPromise = null;

export const fetchTimezoneSettings = async () => {
  if (timezoneSettingsCache) {
    return timezoneSettingsCache;
  }

  if (timezoneSettingsPromise) {
    return timezoneSettingsPromise;
  }

  const { getPublicTimezoneSettings } = await import('../apis/Admin');

  timezoneSettingsPromise = getPublicTimezoneSettings()
    .then((settings) => {
      timezoneSettingsCache = settings;
      timezoneSettingsPromise = null;
      console.log('[Timezone] Settings loaded:', {
        timezone: settings.default_timezone,
        displayName: settings.default_timezone_display_name,
        dateFormat: settings.date_format,
        showUtc: settings.also_show_times_in_utc,
        secondaryTimezone: settings.secondary_timezone,
      });
      return settings;
    })
    .catch((error) => {
      console.warn('Failed to fetch timezone settings, using defaults:', error);
      timezoneSettingsPromise = null;
      return {
        default_timezone: 'UTC',
        default_timezone_display_name: 'UTC',
        also_show_times_in_utc: false,
        secondary_timezone: null,
        secondary_timezone_display_name: null,
        date_format: 'YYYY-MM-DD',
      };
    });

  return timezoneSettingsPromise;
};


export const clearTimezoneSettingsCache = () => {
  timezoneSettingsCache = null;
  timezoneSettingsPromise = null;
};

/**
 * Format a date string with timezone information
 * @param {string|Date} dateInput - ISO date string or Date object
 * @param {Object} options - Formatting options
 * @param {boolean} options.includeTime - Whether to include time (default: true)
 * @param {boolean} options.forceSettings - Force fetch fresh settings (default: false)
 * @returns {Promise<string>} Formatted date string with timezone label
 */
export const formatDateWithTimezone = async (
  dateInput,
  options = {}
) => {
  const { includeTime = true, forceSettings = false } = options;

  if (!dateInput) {
    return '—';
  }

  try {
    if (forceSettings) {
      clearTimezoneSettingsCache();
    }

    const settings = await fetchTimezoneSettings();

    const date = dateInput instanceof Date ? dateInput : new Date(dateInput);

    if (isNaN(date.getTime())) {
      return '—';
    }

    const defaultTz = settings.default_timezone || 'UTC';
    const defaultTzDisplay = settings.default_timezone_display_name || 'UTC';
    const showUtc = settings.also_show_times_in_utc || false;
    const secondaryTz = settings.secondary_timezone;
    const secondaryTzDisplay = settings.secondary_timezone_display_name;
    const dateFormat = settings.date_format || 'YYYY-MM-DD';

    let formattedDate = formatDateInTimezone(date, defaultTz, includeTime, dateFormat);

    const shortTzLabel = getShortTimezoneLabel(defaultTzDisplay);
    formattedDate += ` ${shortTzLabel}`;
    
    console.log('[Timezone] Date formatted:', {
      input: dateInput,
      timezone: defaultTz,
      format: dateFormat,
      result: formattedDate,
    });

    
    if (!showUtc && secondaryTz && secondaryTzDisplay) {
      const secondaryFormatted = formatDateInTimezone(
        date,
        secondaryTz,
        includeTime,
        dateFormat,
      );
      const shortSecondaryLabel = getShortTimezoneLabel(secondaryTzDisplay);
      formattedDate += ` / ${secondaryFormatted} ${shortSecondaryLabel}`;
    }

    return formattedDate;
  } catch (error) {
    console.error('Error formatting date with timezone:', error);
    const date = dateInput instanceof Date ? dateInput : new Date(dateInput);
    if (isNaN(date.getTime())) {
      return '—';
    }
    const isoString = date.toISOString();
    const formatted = includeTime
      ? isoString.replace('T', ' ').slice(0, 19)
      : isoString.split('T')[0];
    return `${formatted} UTC`;
  }
};

/**
 * Format a date according to the date format pattern
 * @param {string} dateFormat - Format pattern (e.g., 'YYYY-MM-DD', 'MM/DD/YYYY', 'DD-MM-YYYY')
 * @param {string} year - Year value
 * @param {string} month - Month value
 * @param {string} day - Day value
 * @param {string} hour - Hour value (optional)
 * @param {string} minute - Minute value (optional)
 * @param {string} second - Second value (optional)
 * @param {boolean} includeTime - Whether to include time
 * @returns {string} Formatted date string
 */
const applyDateFormat = (dateFormat, year, month, day, hour, minute, second, includeTime) => {
  if (!dateFormat) {
    return includeTime
      ? `${year}-${month}-${day} ${hour}:${minute}:${second}`
      : `${year}-${month}-${day}`;
  }

  let formatted = dateFormat;
  
  const placeholders = {
    YEAR: '__YEAR__',
    MONTH: '__MONTH__',
    DAY: '__DAY__',
    HOUR: '__HOUR__',
    MINUTE: '__MINUTE__',
    SECOND: '__SECOND__',
  };

  formatted = formatted.replace(/YYYY/g, placeholders.YEAR);
  
  const datePart = formatted.split(/\s|HH|hh|:/)[0]; 
  const timePart = formatted.substring(datePart.length);
  
  const datePartReplaced = datePart.replace(/MM/g, placeholders.MONTH);
  formatted = datePartReplaced + timePart;
  
  formatted = formatted.replace(/DD/g, placeholders.DAY);
  formatted = formatted.replace(/dd/g, placeholders.DAY);
  
  // Replace time parts if includeTime is true
  if (includeTime) {
    formatted = formatted.replace(/HH/g, placeholders.HOUR);
    formatted = formatted.replace(/hh/g, placeholders.HOUR);
    // Now replace mm (minute) - this should be in time part
    formatted = formatted.replace(/mm/g, placeholders.MINUTE);
    formatted = formatted.replace(/ss/g, placeholders.SECOND);
  } else {
    // Remove time parts if not including time
    formatted = formatted.replace(/\s*HH?:?mm?:?ss?/gi, '');
    formatted = formatted.replace(/\s*hh?:?mm?:?ss?/gi, '');
  }
  
  formatted = formatted.replace(placeholders.YEAR, year);
  formatted = formatted.replace(placeholders.MONTH, month);
  formatted = formatted.replace(placeholders.DAY, day);
  
  if (includeTime) {
    formatted = formatted.replace(placeholders.HOUR, hour || '00');
    formatted = formatted.replace(placeholders.MINUTE, minute || '00');
    formatted = formatted.replace(placeholders.SECOND, second || '00');
  }

  // If time is included but format doesn't have time pattern, append it
  if (includeTime && hour && minute && second) {
    const hasTimePattern = /HH|hh|mm|ss/.test(dateFormat);
    if (!hasTimePattern) {
      formatted += ` ${hour}:${minute}:${second}`;
    }
  }

  return formatted;
};

/**
 * Format a date in a specific timezone
 * @param {Date} date - Date object
 * @param {string} timezone - IANA timezone name (e.g., 'Europe/London', 'Asia/Kolkata')
 * @param {boolean} includeTime - Whether to include time
 * @param {string} dateFormat - Date format pattern (e.g., 'YYYY-MM-DD', 'MM/DD/YYYY')
 * @returns {string} Formatted date string
 */
const formatDateInTimezone = (date, timezone, includeTime = true, dateFormat = null) => {
  try {
    const formatter = new Intl.DateTimeFormat('en-US', {
      timeZone: timezone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      ...(includeTime && {
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false,
      }),
    });

    const parts = formatter.formatToParts(date);
    const year = parts.find((p) => p.type === 'year').value;
    const month = parts.find((p) => p.type === 'month').value;
    const day = parts.find((p) => p.type === 'day').value;

    let hour, minute, second;
    if (includeTime) {
      hour = parts.find((p) => p.type === 'hour').value;
      minute = parts.find((p) => p.type === 'minute').value;
      second = parts.find((p) => p.type === 'second').value;
    }

    return applyDateFormat(dateFormat, year, month, day, hour, minute, second, includeTime);
  } catch (error) {
    console.warn(`Error formatting date in timezone ${timezone}:`, error);
    const isoString = date.toISOString();
    return includeTime
      ? isoString.replace('T', ' ').slice(0, 19)
      : isoString.split('T')[0];
  }
};

/**
 * Synchronous version that uses cached settings
 * Use this when you're sure settings are already loaded
 * @param {string|Date} dateInput - ISO date string or Date object
 * @param {Object} options - Formatting options
 * @returns {string} Formatted date string with timezone label
 */
export const formatDateWithTimezoneSync = (dateInput, options = {}) => {
  const { includeTime = true } = options;

  if (!dateInput) {
    return '—';
  }

  if (!timezoneSettingsCache) {
    const date = dateInput instanceof Date ? dateInput : new Date(dateInput);
    if (isNaN(date.getTime())) {
      return '—';
    }
    const isoString = date.toISOString();
    const formatted = includeTime
      ? isoString.replace('T', ' ').slice(0, 19)
      : isoString.split('T')[0];
    return `${formatted} UTC`;
  }

  try {
    const settings = timezoneSettingsCache;
    const date = dateInput instanceof Date ? dateInput : new Date(dateInput);

    if (isNaN(date.getTime())) {
      return '—';
    }

    const defaultTz = settings.default_timezone || 'UTC';
    const defaultTzDisplay = settings.default_timezone_display_name || 'UTC';
    const showUtc = settings.also_show_times_in_utc || false;
    const secondaryTz = settings.secondary_timezone;
    const secondaryTzDisplay = settings.secondary_timezone_display_name;
    const dateFormat = settings.date_format || 'YYYY-MM-DD';

    let formattedDate = formatDateInTimezone(date, defaultTz, includeTime, dateFormat);
    
    const shortTzLabel = getShortTimezoneLabel(defaultTzDisplay);
    formattedDate += ` ${shortTzLabel}`;

    if (!showUtc && secondaryTz && secondaryTzDisplay) {
      const secondaryFormatted = formatDateInTimezone(
        date,
        secondaryTz,
        includeTime,
        dateFormat,
      );
      const shortSecondaryLabel = getShortTimezoneLabel(secondaryTzDisplay);
      formattedDate += ` / ${secondaryFormatted} ${shortSecondaryLabel}`;
    }

    return formattedDate;
  } catch (error) {
    console.error('Error formatting date with timezone (sync):', error);
    const date = dateInput instanceof Date ? dateInput : new Date(dateInput);
    if (isNaN(date.getTime())) {
      return '—';
    }
    const isoString = date.toISOString();
    const formatted = includeTime
      ? isoString.replace('T', ' ').slice(0, 19)
      : isoString.split('T')[0];
    return `${formatted} UTC`;
  }
};

